import { useCallback, useMemo } from 'react';

import { useProfile, type FeaturePermission } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';

import { meetsPermission } from '../helpers/permissionLevel.js';

type AiHubFeature = {
  scheduledChatFeature: boolean;
  knowledgeBaseDownloadFeature: boolean;
  knowledgeBaseUploadFeature: boolean;
  knowledgeBaseDeleteFeature: boolean;
  chartDownloadFeature: boolean;
  tableDownloadFeature: boolean;
  reportDownloadFeature: boolean;
};

const permissionConfig: Record<keyof AiHubFeature, FeaturePermission> = {
  scheduledChatFeature: 'modify',
  knowledgeBaseDownloadFeature: 'modify',
  knowledgeBaseUploadFeature: 'modify',
  knowledgeBaseDeleteFeature: 'modify',
  chartDownloadFeature: 'modify',
  tableDownloadFeature: 'modify',
  reportDownloadFeature: 'modify',
};

/** The workspace's `aiHubFeature` level, `no_access` when it is missing under the flag. */
const useAiHubPermissionLevel = () => {
  const { profileService } = useProfile();
  const ff = useFlags('@mint/arm', ['aiHubPermission']);

  const permissions = profileService.getPermissions();

  const permissionLevelFallback = ff.aiHubPermission.enabled
    ? 'no_access'
    : ('full_control' as FeaturePermission);
  const aiHubPermissionLevel =
    permissions['aiHubFeature'] ?? permissionLevelFallback;

  return aiHubPermissionLevel;
};

const useAiHubPermissions = () => {
  const aiHubPermissionLevel = useAiHubPermissionLevel();

  const hasAccess = useCallback(
    (feature: keyof AiHubFeature) =>
      meetsPermission(aiHubPermissionLevel, permissionConfig[feature]),
    [aiHubPermissionLevel],
  );

  return useMemo(
    () =>
      ({
        canScheduleChat: hasAccess('scheduledChatFeature'),
        canDownloadKBFile: hasAccess('knowledgeBaseDownloadFeature'),
        canUploadKBFile: hasAccess('knowledgeBaseUploadFeature'),
        canDeleteKBFile: hasAccess('knowledgeBaseDeleteFeature'),
        canDownloadTable: hasAccess('tableDownloadFeature'),
        canDownloadChart: hasAccess('chartDownloadFeature'),
        canDownloadReport: hasAccess('reportDownloadFeature'),
      }) satisfies Record<string, boolean>,
    [hasAccess],
  );
};

export { useAiHubPermissionLevel, useAiHubPermissions };
